import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { liveQuery } from 'dexie'
import { db } from './db.ts'
import { syncOutbox, pruneOutbox } from './outbox.ts'
import { refreshSnapshot } from './cache.ts'

/**
 * One place the app asks "are we online, and how many drafts are waiting?" — and
 * the place that reacts to the connection coming back: flush the queue, refresh
 * the offline snapshot. The status bar reads from here (SPEC 5.7).
 */

interface OfflineState {
  online: boolean
  queueCount: number
  sync: () => Promise<void>
}

const Ctx = createContext<OfflineState>({ online: true, queueCount: 0, sync: async () => {} })

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(() => navigator.onLine)
  const [queueCount, setQueueCount] = useState(0)

  const sync = useCallback(async () => {
    if (!navigator.onLine) return
    await syncOutbox()
    await pruneOutbox()
    await refreshSnapshot()
  }, [])

  // Keep the queue badge live. A Dexie liveQuery fires on any change to the
  // outbox, from any part of the app — no manual notify plumbing to get wrong.
  useEffect(() => {
    const sub = liveQuery(() => db.outbox.where('status').equals('pending').count()).subscribe(setQueueCount)
    return () => sub.unsubscribe()
  }, [])

  // React to connectivity. On coming back, drain the queue and refresh the cache.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true)
      void sync()
    }
    const goOffline = () => setOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    // Also sync on mount (a fresh load after being offline, or a normal login).
    void sync()
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [sync])

  return <Ctx.Provider value={{ online, queueCount, sync }}>{children}</Ctx.Provider>
}

export function useOffline(): OfflineState {
  return useContext(Ctx)
}
